import React from "react";
import { Navbar, Container, Nav, NavDropdown } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import {
  FaInstagram,
  FaLinkedin,
  FaFacebookSquare,
  FaGithubSquare,
  FaYoutubeSquare,
  FaStackOverflow,
  FaRegCopyright,
} from "react-icons/fa";
import "./SiteLayout.css";

const professionalLinks = [
  { label: "Resume", to: "/professional/resume" },
  { label: "Projects", to: "/professional/projects" },
  { label: "Financial Risk", to: "/professional/apps/financial-risk" },
  { label: "Maze Generator", to: "/professional/apps/maze-generator" },
];

const religiousLinks = [
  { label: "Videos", to: "/religious/videos" },
  { label: "Photos", to: "/religious/photos" },
  { label: "Calendar", to: "/religious/calendar" },
  { label: "Panchang Intake", to: "/religious/panchang-intake" },
  { label: "Puja List Builder", to: "/religious/apps/puja-list-builder" },
  { label: "Birth Chart Names", to: "/religious/apps/birth-chart-names" },
];

const footerGroups = [
  {
    title: "Home",
      links: [
        { label: "Home", to: "/" },
      { label: "About", to: "/professional/resume" },
      { label: "Spirituality", to: "/religious/videos" },
      { label: "Merch", to: "/merch" },
    ],
  },
  {
    title: "Professional",
    links: professionalLinks,
  },
  {
    title: "Religious",
    links: religiousLinks,
  },
  {
    title: "Support",
    links: [
      { label: "Support", to: "/support" },
      { label: "Privacy", to: "/privacy" },
    ],
  },
];

const socialLinks = [
  {
    href: "https://www.linkedin.com/in/vishwaram-maharaj-98110173",
    title: "LinkedIn",
    icon: <FaLinkedin className="social-media" />,
  },
  {
    href: "https://www.facebook.com/vishwaram",
    title: "Facebook",
    icon: <FaFacebookSquare className="social-media" />,
  },
  {
    href: "https://www.instagram.com/indian.gorilla/",
    title: "Instagram",
    icon: <FaInstagram className="social-media" />,
  },
  {
    href: "https://www.youtube.com/c/vishwarammaharaj",
    title: "YouTube",
    icon: <FaYoutubeSquare className="social-media" />,
  },
  {
    href: "https://github.com/vishwaramm",
    title: "GitHub",
    icon: <FaGithubSquare className="social-media" />,
  },
  {
    href: "https://stackoverflow.com/users/2488939/the-mahahaj",
    title: "StackOverflow",
    icon: <FaStackOverflow className="social-media" />,
  },
];

function SiteLayout({ children }) {
  const location = useLocation();
  const isHome = location.pathname === "/" || location.pathname === "/home";
  const isPrimaryActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const isPrimaryPage = [
    "/professional/resume",
    "/professional/projects",
    "/religious/videos",
    "/religious/photos",
    "/support",
  ].some(isPrimaryActive);

  const isMoreActive =
    !isPrimaryPage &&
    (location.pathname.startsWith("/professional") ||
      location.pathname.startsWith("/religious") ||
      location.pathname === "/merch" ||
      location.pathname === "/privacy");

  return (
    <>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <Navbar expand="lg" variant="dark" className="site-nav" as="header">
        <Container>
          <Navbar.Brand as={Link} to="/" className="brand-mark">
            <span className="brand-monogram">VM</span>
            <span className="brand-copy">
              Vishwaram Maharaj
              <small>Home</small>
            </span>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="responsive-navbar-nav" aria-label="Toggle navigation" />
          <Navbar.Collapse id="responsive-navbar-nav">
            <Nav className="me-auto">
              <Nav.Link as={Link} to="/" active={isHome}>
                Home
              </Nav.Link>
              <Nav.Link as={Link} to="/professional/resume" active={isPrimaryActive("/professional/resume")}>
                About
              </Nav.Link>
              <Nav.Link as={Link} to="/professional/projects" active={isPrimaryActive("/professional/projects")}>
                Technology
              </Nav.Link>
              <Nav.Link as={Link} to="/religious/videos" active={isPrimaryActive("/religious/videos")}>
                Spirituality
              </Nav.Link>
              <Nav.Link as={Link} to="/religious/photos" active={isPrimaryActive("/religious/photos")}>
                Media
              </Nav.Link>
              <Nav.Link as={Link} to="/support" active={isPrimaryActive("/support")}>
                Contact
              </Nav.Link>
              <NavDropdown title="More" id="more-dropdown" className="site-nav-dropdown" active={isMoreActive}>
                {professionalLinks
                  .filter((link) => !["/professional/resume", "/professional/projects"].includes(link.to))
                  .map((link) => (
                  <NavDropdown.Item as={Link} to={link.to} key={link.to}>
                    {link.label}
                  </NavDropdown.Item>
                ))}
                {religiousLinks
                  .filter((link) => !["/religious/videos", "/religious/photos"].includes(link.to))
                  .map((link) => (
                  <NavDropdown.Item as={Link} to={link.to} key={link.to}>
                    {link.label}
                  </NavDropdown.Item>
                ))}
                <NavDropdown.Item as={Link} to="/merch">
                  Merch
                </NavDropdown.Item>
              </NavDropdown>
            </Nav>
            <div className="nav-actions">
              <div className="nav-socials">
                {socialLinks.map((link) => (
                  <a
                    key={link.title}
                    className="nav-social-link"
                    target="_blank"
                    rel="noreferrer"
                    href={link.href}
                    aria-label={link.title}
                    title={link.title}
                  >
                    {link.icon}
                  </a>
                ))}
              </div>
            </div>
          </Navbar.Collapse>
        </Container>
      </Navbar>
      <div className="site-ambient" aria-hidden="true">
        <span className="site-ambient-orb site-ambient-orb-one" />
        <span className="site-ambient-orb site-ambient-orb-two" />
        <span className="site-ambient-orb site-ambient-orb-three" />
        <span className="site-ambient-lightning" />
      </div>
      <div className="site-content" id="main-content">
        {children}
        <footer className="site-footer">
          <div className="site-footer-card">
            <div className="site-footer-links">
              {footerGroups.map((group) => (
                <div className="site-footer-group" key={group.title}>
                  <div className="site-footer-group-title">{group.title}</div>
                  <div className="site-footer-group-links">
                    {group.links.map((link) => (
                    link.to.includes("#") ? (
                      <a key={link.to} href={link.to} className="site-footer-link">{link.label}</a>
                    ) : (
                      <Link key={link.to} to={link.to} className="site-footer-link">{link.label}</Link>
                    )
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <p className="site-footer-meta">
              <FaRegCopyright
                style={{ fontSize: ".8em", top: "-1px", position: "relative" }}
              />{" "}
              {new Date().getFullYear()}{" "}
              <span className="highlight">Vishwaram Maharaj</span>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}

export default SiteLayout;
